import { useState, useEffect } from 'react';
import { Card, CardBody, Button, Input } from '@components/ui';
import { useCompanies } from '@features/companies/hooks/useCompanies';
import { reportService } from '../services/reportService';
import type { AludraBranchOffice } from '../types/report.types';
import { showSuccess, showError, showWarning } from '@/utils/sweetAlert';
import { useAuthStore } from '@features/auth/store/authStore';
import { getHomeRouteForRole } from '@lib/utils/roleNavigation';

// Helper function to get date in YYYY-MM-DD format
const getFormattedDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// El backend responde 404 cuando Aludra no encuentra facturas para el filtro (no es un error)
const SIN_DATOS_MENSAJE = 'No hay facturas para el período o sucursal seleccionados';
const esSinDatos = (error: unknown): boolean =>
  (error as { response?: { status?: number } })?.response?.status === 404;

// Get today's date
const today = new Date();
const todayFormatted = getFormattedDate(today);

// Get date one month ago
const oneMonthAgo = new Date();
oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
const oneMonthAgoFormatted = getFormattedDate(oneMonthAgo);

export function ReportsPage() {
  const { selectedCompany, companies } = useCompanies({ navigateOnSelect: false });
  const { user } = useAuthStore();
  const homeRoute = getHomeRouteForRole(user?.role);

  console.log('[ReportsPage] selectedCompany:', selectedCompany);
  console.log('[ReportsPage] companies:', companies);

  // Estados para Documentos emitidos - with default date (today)
  const [invoiceDate, setInvoiceDate] = useState(todayFormatted);
  const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);

  // Estados para Reporte Z - with default dates (one month ago to today)
  const [zStartDate, setZStartDate] = useState(oneMonthAgoFormatted);
  const [zEndDate, setZEndDate] = useState(todayFormatted);
  const [zStoreCodes, setZStoreCodes] = useState<string[]>([]);
  const [isGeneratingZ, setIsGeneratingZ] = useState(false);

  // Estados para Balance General - with default dates (one month ago to today)
  const [balanceInitDate, setBalanceInitDate] = useState(oneMonthAgoFormatted);
  const [balanceEndDate, setBalanceEndDate] = useState(todayFormatted);
  const [balanceStoreCodes, setBalanceStoreCodes] = useState<string[]>([]);
  const [isGeneratingBalance, setIsGeneratingBalance] = useState(false);

  // Estados para sucursales
  const [branchOffices, setBranchOffices] = useState<AludraBranchOffice[]>([]);
  const [isLoadingBranches, setIsLoadingBranches] = useState(false);

  // Cargar sucursales al montar el componente
  useEffect(() => {
    const loadBranchOffices = async () => {
      const companyId = selectedCompany?.id;

      if (!companyId) {
        console.log('No company selected, skipping branch offices load');
        return;
      }

      console.log('Loading branch offices for company:', companyId);

      try {
        setIsLoadingBranches(true);
        const response = await reportService.searchBranchOffices(companyId);

        console.log('Branch offices response:', response);

        if (response.Status?.Code === 200 && response.Data && Array.isArray(response.Data)) {
          // Filter only active branch offices
          const activeBranches = response.Data.filter(b => b.IsActive);
          console.log('Active branches found:', activeBranches.length);
          setBranchOffices(activeBranches);
        } else {
          console.warn('Invalid response structure:', response);
        }
      } catch (error) {
        console.error('Error loading branch offices:', error);
      } finally {
        setIsLoadingBranches(false);
      }
    };

    loadBranchOffices();
  }, [selectedCompany?.id]);

  const handleGenerateIssuedDocuments = async () => {
    const companyId = selectedCompany?.id;
    if (!companyId) {
      showWarning('Atención', 'No hay compañía seleccionada');
      return;
    }

    try {
      setIsGeneratingExcel(true);

      const request = {
        FileName: 'Reporte_facturas',
        ReportName: 'DailyElectronicInvoice',
        Filters: {
          CompanyId: companyId,
          ReportDate: invoiceDate,
        },
      };

      console.log('Generating issued documents report:', request);

      const blob = await reportService.generateExcelReport(request);
      reportService.downloadExcelReport(blob, request.FileName);

      showSuccess('¡Éxito!', 'Reporte generado exitosamente');
    } catch (error) {
      if (esSinDatos(error)) {
        showWarning('Sin datos', SIN_DATOS_MENSAJE);
        return;
      }
      console.error('Error generating report:', error);
      showError('Error', 'No se pudo generar el reporte');
    } finally {
      setIsGeneratingExcel(false);
    }
  };

  const handleGenerateZReport = async () => {
    const companyId = selectedCompany?.id;
    const companyCode = selectedCompany?.code;

    if (!companyId) {
      showWarning('Atención', 'No hay compañía seleccionada');
      return;
    }

    if (!companyCode) {
      showWarning('Atención', 'La compañía no tiene código configurado');
      return;
    }

    if (!zStartDate || !zEndDate) {
      showWarning('Atención', 'Por favor seleccione las fechas de inicio y fin');
      return;
    }

    try {
      setIsGeneratingZ(true);

      const filters: any = {
        CompanyId: companyId,
        StartDate: `${zStartDate} 00:00:00`,
        EndDate: `${zEndDate} 23:59:59`,
      };

      // Solo agregar StoreCode si hay códigos seleccionados
      if (zStoreCodes.length > 0) {
        filters.StoreCode = zStoreCodes[0];
      }

      const request = {
        RenderType: 1, // 1 = PDF
        FileName: 'Reporte_Z',
        ReportName: 'FEZReport',
        CompanyCode: companyCode,
        Filters: filters,
      };

      console.log('Generating Z report:', request);

      const blob = await reportService.generateZReport(request);
      reportService.downloadPdfReport(blob, request.FileName);

      showSuccess('¡Éxito!', 'Reporte Z generado exitosamente');
    } catch (error) {
      console.error('Error generating Z report:', error);
      showError('Error', 'No se pudo generar el Reporte Z');
    } finally {
      setIsGeneratingZ(false);
    }
  };

  const handleGenerateBalanceSheet = async () => {
    const companyId = selectedCompany?.id;
    if (!companyId) {
      showWarning('Atención', 'No hay compañía seleccionada');
      return;
    }

    if (!balanceInitDate || !balanceEndDate) {
      showWarning('Atención', 'Por favor seleccione las fechas de inicio y fin');
      return;
    }

    try {
      setIsGeneratingBalance(true);

      const filters: any = {
        CompanyId: companyId,
        StartDate: `${balanceInitDate} 00:00:00`,
        EndDate: `${balanceEndDate} 23:59:59`,
      };

      // Solo agregar StoreCode si hay códigos seleccionados
      if (balanceStoreCodes.length > 0) {
        filters.StoreCode = balanceStoreCodes[0];
      }

      const request = {
        FileName: 'Reporte_Balance_General',
        ReportName: 'FEInvoiceDetails',
        Filters: filters,
      };

      console.log('Generating balance sheet report:', request);

      const blob = await reportService.generateExcelReport(request);
      reportService.downloadExcelReport(blob, request.FileName);

      showSuccess('¡Éxito!', 'Reporte de Balance General generado exitosamente');
    } catch (error) {
      if (esSinDatos(error)) {
        showWarning('Sin datos', SIN_DATOS_MENSAJE);
        return;
      }
      console.error('Error generating balance sheet:', error);
      showError('Error', 'No se pudo generar el reporte de Balance General');
    } finally {
      setIsGeneratingBalance(false);
    }
  };


  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-secondary-900 dark:text-white">
          Administrador de reportes
        </h1>
        <nav className="mt-2 flex" aria-label="Breadcrumb">
          <ol className="flex items-center space-x-2 text-sm">
            <li>
              <a
                href={homeRoute}
                className="text-secondary-500 hover:text-secondary-700 dark:text-secondary-400"
              >
                Inicio
              </a>
            </li>
            <li className="flex items-center">
              <span className="mx-2 text-secondary-400">/</span>
              <span className="font-medium text-secondary-900 dark:text-white">
                Reportes
              </span>
            </li>
          </ol>
        </nav>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Title and Animation */}
        <div className="lg:col-span-1 space-y-4">
          <Card>
            <CardBody className="p-6">
              <h1 className="text-4xl font-bold text-secondary-900 dark:text-white mb-6">
                Reportes
              </h1>
            </CardBody>
          </Card>

          <Card>
            <CardBody className="flex justify-center items-center p-6" style={{ minHeight: '300px' }}>
              {/* Placeholder for animation - In production, this would be a Lottie animation */}
              <div className="text-center">
                <svg
                  className="w-48 h-48 mx-auto text-primary-200 dark:text-primary-800"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <p className="text-sm text-secondary-500 dark:text-secondary-400 mt-4">
                  Generador de reportes
                </p>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Right Column - Reports */}
        <div className="lg:col-span-2 space-y-4" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
          {/* Documentos emitidos */}
          <Card>
            <div className="bg-white dark:bg-secondary-800 px-6 py-4 border-b border-secondary-200 dark:border-secondary-700">
              <h5 className="text-lg font-bold text-secondary-900 dark:text-white">
                Documentos emitidos
              </h5>
            </div>
            <CardBody>
              <div className="mb-4">
                <p className="text-sm text-secondary-600 dark:text-secondary-400">
                  Excel con las facturas emitidas correctamente en una fecha.
                </p>
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className="text-secondary-500 dark:text-secondary-400">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  </span>
                  <Input
                    type="date"
                    value={invoiceDate}
                    onChange={(e) => setInvoiceDate(e.target.value)}
                    className="flex-1"
                    required
                  />
                </div>
                <Button
                  data-guide="Generar Reporte Documentos"
                  variant="primary"
                  className="w-full"
                  onClick={handleGenerateIssuedDocuments}
                  disabled={!invoiceDate || isGeneratingExcel}
                >
                  {isGeneratingExcel ? 'Generando...' : 'Generar reporte'}
                </Button>
              </div>
            </CardBody>
          </Card>

          {/* Reporte Z */}
          <Card>
            <div className="bg-white dark:bg-secondary-800 px-6 py-4 border-b border-secondary-200 dark:border-secondary-700">
              <h5 className="text-lg font-bold text-secondary-900 dark:text-white">
                Reporte Z
              </h5>
            </div>
            <CardBody>
              <div className="mb-4">
                <p className="text-sm text-secondary-600 dark:text-secondary-400">
                  PDF con el total de las facturas emitidas durante un rango de fechas.
                </p>
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                      Fecha inicio
                    </label>
                    <div className="flex items-center gap-2">
                      <span className="text-secondary-500 dark:text-secondary-400">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                      </span>
                      <Input
                        type="date"
                        value={zStartDate}
                        onChange={(e) => setZStartDate(e.target.value)}
                        className="flex-1"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                      Fecha fin
                    </label>
                    <div className="flex items-center gap-2">
                      <span className="text-secondary-500 dark:text-secondary-400">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                      </span>
                      <Input
                        type="date"
                        value={zEndDate}
                        onChange={(e) => setZEndDate(e.target.value)}
                        className="flex-1"
                        required
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                    Filtro por sucursales (opcional)
                  </label>
                  {isLoadingBranches ? (
                    <div className="text-sm text-secondary-500">Cargando sucursales...</div>
                  ) : branchOffices.length === 0 ? (
                    <div className="text-sm text-secondary-500">No hay sucursales disponibles</div>
                  ) : (
                    <div className="max-h-40 overflow-y-auto border border-secondary-300 dark:border-secondary-600 rounded-md p-3 bg-white dark:bg-secondary-700">
                      {branchOffices.map((branch) => (
                        <label
                          key={branch.BranchOfficeId}
                          className="flex items-center gap-2 py-1 cursor-pointer hover:bg-secondary-50 dark:hover:bg-secondary-600 px-2 rounded"
                        >
                          <input
                            type="checkbox"
                            value={branch.Code}
                            checked={zStoreCodes.includes(branch.Code)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setZStoreCodes([...zStoreCodes, branch.Code]);
                              } else {
                                setZStoreCodes(zStoreCodes.filter(code => code !== branch.Code));
                              }
                            }}
                            className="w-4 h-4 text-primary-600 border-secondary-300 rounded focus:ring-primary-500"
                          />
                          <span className="text-sm text-secondary-900 dark:text-white">
                            {branch.Code} - {branch.Description}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                  {zStoreCodes.length > 0 && (
                    <p className="mt-1 text-xs text-secondary-600 dark:text-secondary-400">
                      {zStoreCodes.length} sucursal{zStoreCodes.length !== 1 ? 'es' : ''} seleccionada{zStoreCodes.length !== 1 ? 's' : ''}
                    </p>
                  )}
                </div>

                <Button
                  data-guide="Generar Reporte Z"
                  variant="primary"
                  className="w-full"
                  onClick={handleGenerateZReport}
                  disabled={!zStartDate || !zEndDate || isGeneratingZ}
                >
                  {isGeneratingZ ? 'Generando...' : 'Generar reporte'}
                </Button>
              </div>
            </CardBody>
          </Card>

          {/* Balance general */}
          <Card>
            <div className="bg-white dark:bg-secondary-800 px-6 py-4 border-b border-secondary-200 dark:border-secondary-700">
              <h5 className="text-lg font-bold text-secondary-900 dark:text-white">
                Balance general
              </h5>
            </div>
            <CardBody>
              <div className="mb-4">
                <p className="text-sm text-secondary-600 dark:text-secondary-400">
                  Excel con todas las facturas registradas en el sistema según un rango de tiempo.
                </p>
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                      Fecha inicio
                    </label>
                    <div className="flex items-center gap-2">
                      <span className="text-secondary-500 dark:text-secondary-400">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                      </span>
                      <Input
                        type="date"
                        value={balanceInitDate}
                        onChange={(e) => setBalanceInitDate(e.target.value)}
                        className="flex-1"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                      Fecha fin
                    </label>
                    <div className="flex items-center gap-2">
                      <span className="text-secondary-500 dark:text-secondary-400">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                      </span>
                      <Input
                        type="date"
                        value={balanceEndDate}
                        onChange={(e) => setBalanceEndDate(e.target.value)}
                        className="flex-1"
                        required
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                    Filtro por sucursales (opcional)
                  </label>
                  {isLoadingBranches ? (
                    <div className="text-sm text-secondary-500">Cargando sucursales...</div>
                  ) : branchOffices.length === 0 ? (
                    <div className="text-sm text-secondary-500">No hay sucursales disponibles</div>
                  ) : (
                    <div className="max-h-40 overflow-y-auto border border-secondary-300 dark:border-secondary-600 rounded-md p-3 bg-white dark:bg-secondary-700">
                      {branchOffices.map((branch) => (
                        <label
                          key={branch.BranchOfficeId}
                          className="flex items-center gap-2 py-1 cursor-pointer hover:bg-secondary-50 dark:hover:bg-secondary-600 px-2 rounded"
                        >
                          <input
                            type="checkbox"
                            value={branch.Code}
                            checked={balanceStoreCodes.includes(branch.Code)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setBalanceStoreCodes([...balanceStoreCodes, branch.Code]);
                              } else {
                                setBalanceStoreCodes(balanceStoreCodes.filter(code => code !== branch.Code));
                              }
                            }}
                            className="w-4 h-4 text-primary-600 border-secondary-300 rounded focus:ring-primary-500"
                          />
                          <span className="text-sm text-secondary-900 dark:text-white">
                            {branch.Code} - {branch.Description}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                  {balanceStoreCodes.length > 0 && (
                    <p className="mt-1 text-xs text-secondary-600 dark:text-secondary-400">
                      {balanceStoreCodes.length} sucursal{balanceStoreCodes.length !== 1 ? 'es' : ''} seleccionada{balanceStoreCodes.length !== 1 ? 's' : ''}
                    </p>
                  )}
                </div>

                <Button
                  data-guide="Generar Balance General"
                  variant="primary"
                  className="w-full"
                  onClick={handleGenerateBalanceSheet}
                  disabled={!balanceInitDate || !balanceEndDate || isGeneratingBalance}
                >
                  {isGeneratingBalance ? 'Generando...' : 'Generar reporte'}
                </Button>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
}
